import React from 'react';
import './AuthCaptureSuccess.css';

/**
 * Confirmation screen shown ONLY for the Authorize & Capture shift payment mode.
 *
 * In this mode the guest's card is charged as a HOLD (funds authorized, not yet
 * captured) and the reservation waits in "awaiting approval" until the venue
 * confirms. The generic payment-success / partial-payment screens don't fit that
 * story ("confirmed!" or "share this link to collect more") — so authorized
 * reservations render this page instead: payment received, reservation pending,
 * wait for the venue to confirm.
 *
 * Props:
 *  - restaurantName   {string}
 *  - restaurantImage  {string}  raw stored path or absolute URL (resolved here)
 *  - date             {string}  ISO/date string (optional)
 *  - time             {string}  "HH:mm" (optional)
 *  - guests           {number}  (optional)
 *  - guestName        {string}  (optional)
 */
const resolveImageUrl = (img) => {
  if (!img) return '';
  return img.startsWith('http')
    ? img
    : `${process.env.REACT_APP_BACKEND_URL || ''}${img}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  if (Number.isNaN(h)) return timeStr;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const AuthCaptureSuccess = ({
  reservationId,
  restaurantName,
  restaurantImage,
  date,
  time,
  guests,
  guestName,
}) => {
  const heroImage = resolveImageUrl(restaurantImage);
  const name = restaurantName || 'the venue';

  return (
    <div className="acs-container">
      <div className="acs-card">
        {/* Restaurant hero photo — changes per restaurant */}
        <div className="acs-hero">
          {heroImage ? (
            <img
              className="acs-hero-img"
              src={heroImage}
              alt={restaurantName || 'Restaurant'}
            />
          ) : (
            <div className="acs-hero-img acs-hero-fallback" aria-hidden="true">
              <i className="bi bi-shop"></i>
            </div>
          )}
          <div className="acs-hero-overlay" aria-hidden="true"></div>
          <div className="acs-hero-name">
            <span className="acs-hero-eyebrow">Reservation at</span>
            <h1>{restaurantName || 'Restaurant'}</h1>
          </div>
        </div>

        {/* Success badge overlapping the hero */}
        <div className="acs-badge">
          <i className="bi bi-check-lg"></i>
        </div>

        <div className="acs-body">
          <h2 className="acs-title">Payment Successful</h2>
          <p className="acs-subtitle">
            Your payment has been received and is securely held.
          </p>

          {/* The heart of the message: pending, awaiting venue confirmation */}
          <div className="acs-pending" role="status">
            <div className="acs-pending-icon">
              <i className="bi bi-hourglass-split"></i>
            </div>
            <div className="acs-pending-text">
              <strong>Reservation Pending</strong>
              <span>Please wait for {name} to confirm your booking.</span>
            </div>
          </div>

          {(reservationId || date || time || guests) && (
            <div className="acs-details">
              {reservationId && (
                <div className="acs-detail">
                  <i className="bi bi-hash"></i>
                  <div>
                    <span className="acs-detail-label">Reservation ID</span>
                    <span className="acs-detail-value">#{reservationId}</span>
                  </div>
                </div>
              )}
              {date && (
                <div className="acs-detail">
                  <i className="bi bi-calendar3"></i>
                  <div>
                    <span className="acs-detail-label">Date</span>
                    <span className="acs-detail-value">{formatDate(date)}</span>
                  </div>
                </div>
              )}
              {time && (
                <div className="acs-detail">
                  <i className="bi bi-clock"></i>
                  <div>
                    <span className="acs-detail-label">Time</span>
                    <span className="acs-detail-value">{formatTime(time)}</span>
                  </div>
                </div>
              )}
              {guests ? (
                <div className="acs-detail">
                  <i className="bi bi-people"></i>
                  <div>
                    <span className="acs-detail-label">Guests</span>
                    <span className="acs-detail-value">
                      {guests} {guests === 1 ? 'Guest' : 'Guests'}
                    </span>
                  </div>
                </div>
              ) : null}
              {guestName && (
                <div className="acs-detail">
                  <i className="bi bi-person"></i>
                  <div>
                    <span className="acs-detail-label">Booked By</span>
                    <span className="acs-detail-value">{guestName}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="acs-note">
            <i className="bi bi-envelope-check"></i>
            <span>
              You&apos;ll receive a confirmation email as soon as {name}{' '}
              approves your reservation. Your card is only charged once the
              booking is confirmed.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthCaptureSuccess;
